import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Phone, Menu, X, IndianRupee, UserRound } from "lucide-react";
import { WHATSAPP_DISPLAY, openPaymentPage } from "@/lib/whatsapp";
import { getCurrentUser, signInWithGoogle } from "@/lib/auth";

const LINKS = [
  { label: "Services", href: "#services" },
  { label: "Pricing", href: "#pricing" },
  { label: "Request & Join", href: "#join" },
  { label: "Why Sharpin", href: "#why" },
  { label: "Terms", href: "#terms" },
];

const Navbar = ({ onBookCare }) => {
  const [open, setOpen] = useState(false);
  const [user, setUser] = useState(null);

  useEffect(() => {
    getCurrentUser().then(setUser);
  }, []);

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      className="fixed top-0 inset-x-0 z-50 backdrop-blur-md bg-white/85 border-b border-slate-200/80"
      data-testid="navbar"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 h-20 flex items-center justify-between">
        <a href="#top" className="flex items-center gap-3" data-testid="nav-logo">
          <img
            src="/logo.jpeg"
            alt="Sharpin Healthcare Services"
            className="h-12 w-12 rounded-full object-cover ring-2 ring-tealbrand/20"
          />
          <div className="leading-tight">
            <span className="font-display font-bold text-lg tracking-tight text-navy block">
              SHARPIN
            </span>
            <span className="text-[10px] uppercase tracking-[0.3em] text-tealbrand font-semibold">
              Healthcare
            </span>
          </div>
        </a>

        <nav className="hidden lg:flex items-center gap-8">
          {LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className="text-sm font-medium text-slate-600 hover:text-tealbrand transition-colors"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-4">
          <button
            onClick={openPaymentPage}
            data-testid="nav-pay-btn"
            className="flex items-center gap-1.5 text-sm font-semibold text-tealbrand-dark hover:text-navy transition-colors"
          >
            <IndianRupee className="h-4 w-4" />
            Pay Now
          </button>
          <a
            href={`tel:${WHATSAPP_DISPLAY}`}
            data-testid="nav-call-btn"
            className="flex items-center gap-2 text-sm font-semibold text-navy hover:text-tealbrand transition-colors"
          >
            <Phone className="h-4 w-4" />
            {WHATSAPP_DISPLAY}
          </a>
          <button
            onClick={onBookCare}
            data-testid="nav-book-care-btn"
            className="bg-navy text-white text-sm font-semibold px-6 py-3 rounded-full hover:bg-tealbrand transition-colors duration-300"
          >
            Book Care
          </button>
          {user ? (
            <Link
              to="/account"
              data-testid="nav-account-link"
              className="flex items-center gap-2 text-sm font-semibold text-navy hover:text-tealbrand transition-colors"
            >
              <UserRound className="h-4 w-4" /> {user.name?.split(" ")[0] || "Account"}
            </Link>
          ) : (
            <button
              onClick={() => signInWithGoogle("/account")}
              data-testid="nav-signin-btn"
              className="flex items-center gap-2 text-sm font-semibold text-slate-600 hover:text-tealbrand transition-colors"
            >
              <UserRound className="h-4 w-4" /> Sign In
            </button>
          )}
        </div>

        <button
          className="lg:hidden p-2"
          onClick={() => setOpen(!open)}
          data-testid="nav-mobile-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden bg-white border-t border-slate-200 px-6 py-6 space-y-4" data-testid="nav-mobile-menu">
          {LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              onClick={() => setOpen(false)}
              className="block text-base font-medium text-slate-700"
              data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            >
              {l.label}
            </a>
          ))}
          <button
            onClick={openPaymentPage}
            data-testid="nav-mobile-pay-btn"
            className="w-full border-2 border-tealbrand/30 text-tealbrand-dark font-semibold px-6 py-3 rounded-full flex items-center justify-center gap-2"
          >
            <IndianRupee className="h-4 w-4" /> Pay Now
          </button>
          <button
            onClick={() => {
              setOpen(false);
              onBookCare();
            }}
            data-testid="nav-mobile-book-care-btn"
            className="w-full bg-navy text-white font-semibold px-6 py-3 rounded-full"
          >
            Book Care
          </button>
          {user ? (
            <Link
              to="/account"
              onClick={() => setOpen(false)}
              data-testid="nav-mobile-account-link"
              className="block text-center text-sm font-semibold text-navy"
            >
              My Account
            </Link>
          ) : (
            <button
              onClick={() => signInWithGoogle("/account")}
              data-testid="nav-mobile-signin-btn"
              className="w-full text-center text-sm font-semibold text-slate-600"
            >
              Sign In with Google
            </button>
          )}
        </div>
      )}
    </motion.header>
  );
};

export default Navbar;
